import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo } from 'react'
import {
  type AgentThreadKind,
  createThread,
  deleteThread,
  getAgentHistory,
  getThreadHealth,
  listThreads,
  renameThread,
} from '@/services/api/agent'

export const agentHistoryKey = (caseId: string, threadId: string) =>
  ['agent', 'history', caseId, threadId] as const

/** Every thread list of the case, whatever its kind: the prefix `agentThreadsKey` builds on. */
export const agentThreadListsKey = (caseId: string) => ['agent', 'threads', caseId] as const

export const agentThreadsKey = (caseId: string, kind?: AgentThreadKind) =>
  [...agentThreadListsKey(caseId), kind ?? 'all'] as const

export const agentThreadHealthKey = (caseId: string, threadId: string) =>
  ['agent', 'thread-health', caseId, threadId] as const

/** Token usage + banner-trigger status for a thread. Refetched on mount and
 *  after each chat turn (invalidated by the host on stream completion).
 *  With `enabled: false` nothing is asked: the chat host calls it in lake scope too. */
export function useThreadHealth(
  caseId: string,
  threadId: string,
  { enabled }: { enabled: boolean }
) {
  return useQuery({
    queryKey: agentThreadHealthKey(caseId, threadId),
    queryFn: () => getThreadHealth(caseId, threadId),
    enabled,
    staleTime: 15_000,
  })
}

const HISTORY_PAGE_SIZE = 30

/** A case thread's messages, a page at a time. With `enabled: false` nothing is asked of the
 *  server: the chat host calls it in lake scope too, where the history is the lake's. */
export function useAgentHistory(
  caseId: string,
  threadId: string,
  { enabled }: { enabled: boolean }
) {
  const query = useInfiniteQuery({
    queryKey: agentHistoryKey(caseId, threadId),
    queryFn: ({ pageParam }) =>
      getAgentHistory(caseId, threadId, {
        limit: HISTORY_PAGE_SIZE,
        before: pageParam ?? undefined,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: lastPage =>
      lastPage.has_more ? (lastPage.next_cursor ?? undefined) : undefined,
    staleTime: 60_000,
    enabled,
  })

  const messages = useMemo(() => {
    const pages = query.data?.pages ?? []
    return [...pages].reverse().flatMap(p => p.messages)
  }, [query.data])

  return {
    messages,
    isLoading: query.isLoading,
    hasMore: query.hasNextPage,
    isFetchingMore: query.isFetchingNextPage,
    fetchMore: query.fetchNextPage,
  }
}

export function useThreadList(caseId: string, kind?: AgentThreadKind) {
  const queryClient = useQueryClient()

  // Invalidate ALL threads queries for the case (any kind) so toggling
  // tabs or creating a new thread refreshes both lists.
  const invalidateAll = () =>
    queryClient.invalidateQueries({ queryKey: agentThreadListsKey(caseId) })

  const query = useQuery({
    queryKey: agentThreadsKey(caseId, kind),
    queryFn: () => listThreads(caseId, kind),
    staleTime: 30_000,
  })

  const create = useMutation({
    mutationFn: () => createThread(caseId),
    onSuccess: invalidateAll,
  })

  const remove = useMutation({
    mutationFn: (threadId: string) => deleteThread(caseId, threadId),
    onSuccess: invalidateAll,
  })

  const rename = useMutation({
    mutationFn: ({ threadId, title }: { threadId: string; title: string }) =>
      renameThread(caseId, threadId, title),
    onSuccess: invalidateAll,
  })

  return {
    threads: query.data ?? [],
    isLoading: query.isLoading,
    create,
    remove,
    rename,
  }
}
